import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";

import { useWindowWidth } from "@react-hook/window-size";
import { PayloadAction } from "@reduxjs/toolkit";

import { useDispatchBeginCheckout } from "@/app/hooks/useDispatchBeginCheckout";
import Button from "@/components/design-system/button";
import ErrorIcon from "public/next-public/common/icons/error-icon.svg";
import ToastSuccessCheckIcon from "public/next-public/common/icons/toast-success-check-icon.svg";
import XIcon from "public/next-public/common/icons/x-close-icon.svg";
import styles from "./toast.module.scss";
import { useToastAutoDismiss } from "./useToastAutoDismiss";

type ToastLocation = "top" | "bottom";

const stackHosts = new Map<ToastLocation, { node: HTMLElement; count: number }>();

function acquireStackHost(location: ToastLocation, className: string): HTMLElement {
  const existing = stackHosts.get(location);
  if (existing) {
    existing.count += 1;
    return existing.node;
  }
  const node = document.createElement("div");
  node.className = className;
  document.body.appendChild(node);
  stackHosts.set(location, { node, count: 1 });
  return node;
}

function releaseStackHost(location: ToastLocation): void {
  const entry = stackHosts.get(location);
  if (!entry) return;
  entry.count -= 1;
  if (entry.count <= 0) {
    entry.node.remove();
    stackHosts.delete(location);
  }
}

function Toast({
  linkHref,
  linkText,
  toastMessage,
  toastSubMessage,
  showToast,
  toastType,
  hideToast,
  showOnMobile = false,
  showToastTime = 3000,
  toastLocation = "top",
  showCloseButton = false,
  showLinkCaret = true,
  onAction,
  actionText,
  persistAcrossNavigation = false,
}: ToastProps) {
  const dispatch = useDispatch();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 475;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const dispatchToClientAndServerContainer = useDispatchBeginCheckout();

  const isSuccess = toastType === "success";
  const isError = toastType === "error";

  const displayLinkText = useMemo(() => {
    if (!linkText) return "";
    return linkText.replace(/\s*>\s*$/, "").trim();
  }, [linkText]);

  const showStatusIcon = isSuccess || isError;
  const showLeadingSpacer = showStatusIcon && showCloseButton;

  const handleClose = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    dispatch(hideToast());
  }, [dispatch, hideToast]);

  const handleOnClick = useCallback(() => {
    dispatchToClientAndServerContainer();
    handleClose();
  }, [dispatchToClientAndServerContainer, handleClose]);

  const handleAction = useCallback(() => {
    if (onAction) {
      onAction();
    }
    handleClose();
  }, [onAction, handleClose]);

  useEffect(() => {
    if (showToast) {
      timeoutRef.current = setTimeout(() => {
        dispatch(hideToast());
      }, showToastTime);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [showToast, dispatch, hideToast, showToastTime]);

  const visible = showToast && (showOnMobile || !isMobile);

  useToastAutoDismiss(visible, handleClose, { identity: hideToast, persistAcrossNavigation });

  const [stackHost, setStackHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!visible) return;
    const locationClass = toastLocation === "top" ? styles.topToast : styles.bottomToast;
    setStackHost(acquireStackHost(toastLocation, `${styles.stack} ${locationClass}`));
    return () => {
      setStackHost(null);
      releaseStackHost(toastLocation);
    };
  }, [visible, toastLocation]);

  if (!visible || !stackHost) return null;

  return createPortal(
    <div className={styles.toastContainer} role={isError ? "alert" : "status"} aria-atomic="true" data-toast-root>
      <div className={styles.headerRow}>
        {showLeadingSpacer ? <span className={styles.leadingSpacer} aria-hidden /> : null}
        <div className={styles.titleCluster}>
          {isSuccess ? (
            <ToastSuccessCheckIcon className={styles.successIcon} width={16} height={16} aria-hidden />
          ) : null}
          {isError ? <ErrorIcon className={styles.errorIcon} width={16} height={16} aria-hidden /> : null}
          <span className={styles.messageText}>{toastMessage}</span>
        </div>
        {showCloseButton ? (
          <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Close">
            <XIcon className={styles.closeIcon} />
          </button>
        ) : null}
      </div>
      {toastSubMessage ? <p className={styles.subMessage}>{toastSubMessage}</p> : null}
      {displayLinkText && linkHref ? (
        <div className={styles.ctaRow}>
          <Button
            variant="tertiary"
            color="blue"
            text={displayLinkText}
            link={{ href: linkHref }}
            showLeftIcon={false}
            showRightIcon={showLinkCaret}
            onClick={handleOnClick}
          />
        </div>
      ) : null}
      {onAction && actionText ? (
        <div className={styles.ctaRow}>
          <button type="button" className={styles.actionButton} onClick={handleAction}>
            <span className={styles.actionButtonText}>{actionText}</span>
          </button>
        </div>
      ) : null}
    </div>,
    stackHost,
  );
}

export type ToastType = "success" | "error";

export type ToastProps = {
  linkText?: string;
  linkHref?: string;
  toastMessage: string;
  toastSubMessage?: string;
  showToast: boolean;
  toastType: string;
  showOnMobile?: boolean;
  /** How long the toast will show in ms */
  showToastTime?: number;
  hideToast: () => PayloadAction<undefined, string>;
  toastLocation?: "top" | "bottom";
  /** Show X close button */
  showCloseButton?: boolean;
  /** Render the caret after the link CTA text. Defaults to true. */
  showLinkCaret?: boolean;
  /** Callback for action button (alternative to link) */
  onAction?: () => void;
  /** Text for action button */
  actionText?: string;
  /**
   * Keeps the toast up across a route change, for toasts raised *because* we are
   * navigating the user (e.g. the session-expiry notice shown alongside the
   * redirect to /login). Every other toast closes on navigation per SPR-2863.
   */
  persistAcrossNavigation?: boolean;
};

export default Toast;
